import React from 'react';
import { useTranslate } from 'ra-core';
import { LineChart, XAxis, YAxis, Legend, Line, CartesianGrid } from 'recharts';
import { format } from 'date-fns';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { CHART_TICK_FONT_SIZE } from '../chartStyles';

interface UsersChartProps {
  title: string;
  chartData: any[];
  dataKey: string;
  lineName: string;
  includePercentage?: boolean;
  percentageLineName?: string;
}

export const UsersChart: React.FC<UsersChartProps> = ({
  title,
  chartData,
  dataKey,
  lineName,
  includePercentage = false,
  percentageLineName,
}) => {
  const translate = useTranslate();
  // A fixed key for the main series: the config keys become CSS variable names (`--color-<key>`)
  const chartConfig = {
    value: { label: lineName, color: 'var(--chart-1)' },
    percentage: {
      label:
        percentageLineName ?? translate('moonstar.dashboard.users.percentage'),
      color: 'var(--chart-3)',
    },
  } satisfies ChartConfig;

  return (
    <Card className="h-[400px] shadow-md">
      <CardHeader className="pb-0">
        <CardTitle className="text-sm font-bold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="min-h-0 flex-1 p-4 pt-0">
        <ChartContainer
          config={chartConfig}
          className="h-full min-h-[250px] w-full aspect-auto"
        >
          <LineChart
            data={chartData}
            margin={{
              top: 0,
              right: 0,
              left: 0,
              bottom: 30,
            }}
          >
            <CartesianGrid horizontal vertical={false} stroke="var(--border)" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(new Date(date), 'EEE dd/MM')}
              tick={{ fontSize: CHART_TICK_FONT_SIZE }}
              angle={-45}
              textAnchor="end"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: CHART_TICK_FONT_SIZE }}
              stroke="var(--color-value)"
              allowDecimals={false}
              tickCount={5}
            />
            {includePercentage && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: CHART_TICK_FONT_SIZE }}
                domain={[0, 100]}
                stroke="var(--color-percentage)"
              />
            )}
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(date) =>
                    format(new Date(date), 'EEE dd/MM/yyyy')
                  }
                />
              }
            />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={7}
              height={1}
              wrapperStyle={{
                paddingTop: 0,
                marginBottom: -40,
              }}
            />
            <Line
              yAxisId="left"
              type="linear"
              dataKey={dataKey}
              name={lineName}
              stroke="var(--color-value)"
              strokeWidth={2}
              dot={false}
            />
            {includePercentage && (
              <Line
                yAxisId="right"
                type="natural"
                dataKey="percentage"
                name={percentageLineName}
                stroke="var(--color-percentage)"
                strokeWidth={2}
                dot={false}
              />
            )}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
